import React from 'react';

// Smooth scroll with buffer for sticky header
const scrollWithBuffer = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, id: string) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) {
    const yOffset = -80; // buffer for sticky header
    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }
};

const HeroBanner: React.FC = () => (
  <section style={{ 
    padding: '150px 0 6rem 0', 
    background: '#fff',
    position: 'relative',
    overflow: 'hidden'
  }}>
    {/* Subtle background gradient */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '100%',
      background: 'radial-gradient(circle at 50% 0%, rgba(34, 58, 94, 0.03) 0%, rgba(255, 255, 255, 0) 70%)',
      pointerEvents: 'none'
    }} />

    <div style={{
      maxWidth: 1200,
      margin: '0 auto',
      padding: '0 2rem',
      position: 'relative',
      '@media (max-width: 768px)': {
        padding: '0 1.5rem'
      }
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: '2rem'
      }}>
        {/* Main headline with glow effect */}
        <h1 style={{ 
          fontSize: '3.5rem', 
          fontWeight: 900, 
          lineHeight: 1.1, 
          letterSpacing: '-0.03em',
          color: '#223A5E',
          margin: 0,
          textShadow: '0 0 30px rgba(34, 58, 94, 0.1)',
          '@media (max-width: 768px)': {
            fontSize: '2.5rem'
          }
        }}>
          Revolutionizing Medical Education with{' '}
          <span style={{
            background: 'linear-gradient(120deg, #223A5E 0%, #2D4379 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            position: 'relative',
            display: 'inline-block'
          }}>
            AI Patient Simulations
          </span>
        </h1>

        {/* Subheadline with subtle animation */}
        <p style={{ 
          fontSize: '1.35rem', 
          color: '#555', 
          maxWidth: '800px',
          margin: 0,
          lineHeight: 1.5,
          '@media (max-width: 768px)': {
            fontSize: '1.15rem'
          }
        }}>
          SILO provides AI-driven patient simulations that enhance clinical reasoning, 
          communication skills, and humanistic healthcare delivery.
        </p>

        {/* CTA Button with hover effect */}
        <a 
          href="#contact" 
          onClick={e => scrollWithBuffer(e, 'contact')}
          style={{ 
            background: '#223A5E',
            color: '#fff', 
            padding: '1rem 2.5rem', 
            borderRadius: '4px', 
            fontWeight: 600, 
            textDecoration: 'none', 
            fontSize: '1.1rem', 
            boxShadow: '0 4px 20px rgba(34,58,94,0.15)',
            display: 'inline-block',
            transition: 'all 0.2s ease',
            marginTop: '1rem',
            ':hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(34,58,94,0.2)'
            }
          }}
        >
          Request Demo &rarr;
        </a>
      </div>
    </div>

    {/* Accessible AI Training Section - new layout */}
    <div id="humanistic" style={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      maxWidth: 1200,
      margin: '6rem auto 0 auto',
      gap: '2.5rem',
      padding: '3.5rem 2rem',
      background: 'linear-gradient(90deg, #223A5E 0%, #2D4379 100%)',
      borderRadius: '28px',
      boxShadow: '0 8px 32px 0 rgba(34,58,94,0.18)',
      position: 'relative',
      overflow: 'hidden',
      '@media (max-width: 768px)': {
        flexDirection: 'column',
        gap: '1.5rem',
        padding: '2rem 1rem',
        textAlign: 'center',
      }
    }}>
      {/* Image placeholder (left) */}
      <div style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '@media (max-width: 768px)': {
          width: '100%',
          marginBottom: '1rem',
        }
      }}>
        <img 
          src="/silomedai-assets/images/humanistic-health-care.png" 
          alt="Humanistic Healthcare Training" 
          style={{
            width: '100%',
            maxWidth: 900,
            height: 'auto',
            borderRadius: '3px',
            objectFit: 'cover',
            background: '#e0e0e0',
            boxShadow: '0 4px 32px 0 rgba(34,58,94,0.18)',
            border: '4px solid #fff',
          }}
        />
      </div>
      {/* Text (right) */}
      <div style={{
        flex: 2,
        textAlign: 'left',
        color: '#fff',
        '@media (max-width: 768px)': {
          textAlign: 'center',
        }
      }}>
        {/* Mission badge */}
        <div style={{
          display: 'inline-block',
          background: 'rgba(255,255,255,0.18)',
          color: '#fff',
          fontWeight: 700,
          fontSize: '1rem',
          borderRadius: '999px',
          padding: '0.35em 1.25em',
          marginBottom: '1.25rem',
          letterSpacing: '0.04em',
          boxShadow: '0 2px 8px rgba(34,58,94,0.10)',
        }}>
          Our Mission
        </div>
        <h2 style={{
          fontSize: '2.5rem',
          fontWeight: 900,
          marginBottom: '1.25rem',
          letterSpacing: '-0.02em',
          color: '#fff',
          lineHeight: 1.1,
          '@media (min-width: 768px)': {
            fontSize: '3.25rem'
          }
        }}>
          Accessible AI Training for<br />
          <span style={{ color: '#7eb6ff', background: 'rgba(255,255,255,0.10)', borderRadius: '0.2em', padding: '0 0.25em', display: 'inline-block', marginTop: '0.15em' }}>Humanistic Healthcare</span>
        </h2>
        <div style={{
          fontSize: '1.25rem',
          fontWeight: 700,
          color: '#d1d8f0',
          marginBottom: '1.25rem',
          fontStyle: 'italic',
          lineHeight: 1.4,
        }}>
          Our mission is to make advanced, compassionate medical training accessible to every learner, everywhere.
        </div>
        <p style={{
          fontSize: '1.15rem',
          color: '#e6eaf6',
          maxWidth: 700,
          margin: '0 0 2rem 0',
          fontWeight: 400,
          lineHeight: 1.5,
          '@media (min-width: 768px)': {
            fontSize: '1.25rem'
          }
        }}>
          We're committed to enhancing medical education through innovative AI technology that develops both clinical reasoning and compassionate patient communication.
        </p>
        <a
          href="#whitepapers"
          style={{
            display: 'inline-block',
            background: '#fff',
            color: '#223A5E',
            fontWeight: 700,
            fontSize: '1.1rem',
            borderRadius: '4px',
            padding: '0.75rem 2rem',
            textDecoration: 'none',
            boxShadow: '0 2px 8px rgba(34,58,94,0.10)',
            marginTop: '0.5rem',
            transition: 'background 0.15s, color 0.15s',
          }}
        >
          Learn More About Our Mission
        </a>
      </div>
    </div>
  </section>
);

export default HeroBanner; 